import React, { useState, useEffect, useContext } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { auth } from '../services/api';
import { ToastContext } from '../App';
import TrainingAcknowledgement from './TrainingAcknowledgement';

function Dashboard() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load collapsed state from localStorage, default to expanded (false)
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const navigate = useNavigate();
  const { showToast } = useContext(ToastContext) || {};

  useEffect(() => {
    // Fetch current user info
    const fetchUser = async () => {
      try {
        const response = await auth.me();
        setUser(response.data);
      } catch (err) {
        console.error('Failed to fetch user:', err);
        
        // Handle different error types
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          if (showToast) {
            showToast('Request timed out. Please check your connection and try again.', 'warning');
          }
        } else if (!err.response) {
          if (showToast) {
            showToast('Network error. Please check your internet connection.', 'error');
          }
        }
        
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [navigate, showToast]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    if (showToast) {
      showToast('You have been logged out.', 'success');
    }
    navigate('/login');
  };

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    // Save to localStorage
    localStorage.setItem('sidebarCollapsed', newState.toString());
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner large"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <TrainingAcknowledgement user={user} />
      
      <header className="dashboard-header">
        <div className="header-left">
          <h1>SimplMeds Review App</h1>
        </div>
        <div className="header-right">
          <span className="user-info">
            {user?.full_name} {user?.credentials && `(${user.credentials})`}
          </span>
          <button onClick={handleLogout} className="btn-secondary">
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-body">
        <aside className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <button 
            className="sidebar-toggle" 
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? '»' : '«'}
          </button>
          <nav>
            <ul>
              <li>
                <Link to="/dashboard/medications" title="Available Medications">
                  <span className="nav-icon">📋</span>
                  {!sidebarCollapsed && <span className="nav-text">Available Medications</span>}
                </Link>
              </li>
              <li>
                <Link to="/dashboard/my-claim" title="My Current Claim">
                  <span className="nav-icon">📝</span>
                  {!sidebarCollapsed && <span className="nav-text">My Current Claim</span>}
                </Link>
              </li>
              {user?.role === 'admin' && (
                <li>
                  <Link to="/dashboard/admin" title="Admin Panel">
                    <span className="nav-icon">⚙️</span>
                    {!sidebarCollapsed && <span className="nav-text">Admin Panel</span>}
                  </Link>
                </li>
              )}
              <li className="help-link">
                <a href="/docs/index.html" target="_blank" rel="noopener noreferrer" title="Help & Training">
                  <span className="nav-icon">📚</span>
                  {!sidebarCollapsed && <span className="nav-text">Help & Training</span>}
                </a>
              </li>
              <li className="help-link">
                <a href="mailto:support@simplmeds.com" title="Contact Support">
                  <span className="nav-icon">💬</span>
                  {!sidebarCollapsed && <span className="nav-text">Contact Support</span>}
                </a>
              </li>
            </ul>
          </nav>
        </aside>

        <main className="dashboard-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Dashboard;
